import _ from 'lodash'
import { z } from 'zod'
import { DateTime } from 'luxon'
import { datestamp } from '../util'
import { BranchDesignator } from '@fusion-advantage/backcompat'
import { CodeNext } from '@fusion-advantage/backcompat'

export enum WorkItemType {
	Appointment = 'appointment',
	Todo = 'todo',
}

export const RTodo = z.object({
	WorkItemType: z.enum(WorkItemType).extract(["Todo"]),
	MemberGUID: z.uuid(),
	MemberName: z.string(),
	EncounterGUID: z.string(),
	// ... dan says he may send codes we don't know about ...
	// we need to show these on the dashboard and allow the form to be
	// viewable, but we lose type inference of the enum
	CodeNext: z.union([z.enum(CodeNext), z.string()]),
	// 20240923 DB provides Date of Service here when possible
	// 20250818 the procs will never send us null; it is only allowed here
	// for a technically nullable DateOfService when creating draft Todos
	CreatedOn: z.nullable(z.iso.date()),
	ReadOnly: z.boolean(),
})
export type Todo = z.infer<typeof RTodo>

// this enum does not represent all of the possibilities;
// when we need to deal with another of the possible values,
// please add it here
export enum ServiceAppointmentStatus {
	CannotComplete = 'Cannot Complete',
	Completed = 'Completed',
	Canceled = 'Canceled',
	Dispatched = 'Dispatched',
	InProgress = 'In Progress',
	Scheduled = 'Scheduled',
	None = 'None',
}

export const RAppointment = z.object({
	WorkItemType: z.enum(WorkItemType).extract(["Appointment"]),
	ServiceAppointmentID: z.string(),
	AppointmentNumber: z.string(),
	MemberGUID: z.nullable(z.uuid()),
	MemberName: z.string(),
	MemberPhones: z.array(z.object({
		Label: z.union([
			z.literal('Secondary'),
			z.literal('Alternate'),
			z.literal('Mobile'),
		]),
		Number: z.nullable(z.string()),
	})),
	StartTime: z.iso.datetime(),
	EndTime: z.iso.datetime(),
	Street: z.nullable(z.string()),
	City: z.nullable(z.string()),
	State: z.nullable(z.string()),
	PostalCode: z.nullable(z.string()),
	Country: z.nullable(z.string()),
	Latitude: z.nullable(z.number()),
	Longitude: z.nullable(z.number()),
	EstimatedTravelTimeMins: z.nullable(z.number()),
	CanTrustGPS: z.boolean(),
	Status: z.enum(ServiceAppointmentStatus),
	// 20260806
	// IsJoinable is actually not optional from the api, but don't want to
	// break the local device cache before resync.  can safely remove the
	// optional part at some later date.
	IsJoinable: z.optional(z.boolean()),
	HasInterpreter: z.boolean(),
	InterpreterNumber: z.nullable(z.string()),
	ClinicianNotes: z.nullable(z.string()),
	EligibleLabs: z.nullable(z.array(z.string())),
	BranchDesignator: z.enum(BranchDesignator),
	ReggieMSAppointmentID: z.nullable(z.string()),
	ClinicianVideoVisitJoinURL: z.nullable(z.string()),
})
export type Appointment = z.infer<typeof RAppointment>

export const appointmentMapUrl = (appt: Appointment) => {
	const addr = encodeURIComponent([
		appt.Street,
		appt.City,
		appt.State,
		appt.PostalCode
	].join(', '))
	return `http://maps.apple.com/?daddr=${addr}&dirflg=d`
}

export const appointmentIsOnDay = (appt: { StartTime: string }, isoDateStr: string): boolean => {
	// we expect that the StartTime from Salesforce (validated above)
	// is a valid ISO 8601 date string
	return DateTime.fromISO(appt.StartTime).toLocal().toISODate() === isoDateStr
}

export const appointmentIsToday = (appt: { StartTime: string }): boolean => {
	return appointmentIsOnDay(appt, datestamp())
}

export const appointmentDurationMinutes = (appt: { StartTime: string, EndTime: string }): number => {
	return DateTime.fromISO(appt.EndTime).diff(DateTime.fromISO(appt.StartTime)).as('minute')
}

export const RWorkItem = z.union([RAppointment, RTodo])
export type WorkItem = Appointment | Todo

export interface AppointmentKey {
	type: WorkItemType.Appointment
	serviceApptId: string
}

export const newAppointmentKey = (item: Appointment): AppointmentKey => {
	return {
		type: item.WorkItemType,
		serviceApptId: item.ServiceAppointmentID,
	}
}

export interface TodoKey {
	type: WorkItemType.Todo
	encounterGuid: string
} 

export type WorkItemKey = TodoKey | AppointmentKey

export const newWorkItemKey = (item: WorkItem): WorkItemKey => {
	switch(item.WorkItemType) {
		case WorkItemType.Todo:
			return {
				type: item.WorkItemType,
				encounterGuid: item.EncounterGUID,
			}
		case WorkItemType.Appointment:
			return newAppointmentKey(item)
	}
}

export const workItemTitle = (item: WorkItem): string => {
	let title = item.MemberName
	if(item.WorkItemType === WorkItemType.Appointment) {
		title = `[${item.AppointmentNumber}] ${item.MemberName}`
	}
	return title 
}

export enum WorkItemAction {
	ViewForm = 'viewForm',
	StartEncounter = 'startEncounter',
	ContinueForm = 'continueForm',
	JoinVideo = 'joinVideo',
	SendReminder = 'sendReminder',
	CancelAppointment = 'cancelAppointment',
	ViewFormAsAuditor = 'viewFormAsAuditor',
	ViewFormAsDocgen = 'viewFormAsDocgen',  // print service
}

export const isWorkItemAction = (action: unknown): action is WorkItemAction => {
	return Object.values(WorkItemAction).includes(action as WorkItemAction)
}
